import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/base/PageHeader';
import Badge from '@/components/base/Badge';
import EmptyState from '@/components/base/EmptyState';
import RowActions from '@/components/base/RowActions';
import { sectors } from '@/types';
import { useData } from '@/context/DataContext';
import { formatTL, formatDate } from '@/lib/format';
import { useApp } from '@/context/AppContext';

export default function Customers() {
  const navigate = useNavigate();
  const { openCreate, openEdit, openDelete } = useApp();
  const { customers } = useData();
  const [query, setQuery] = useState('');
  const [sector, setSector] = useState('');
  const [city, setCity] = useState('');
  const [activeProject, setActiveProject] = useState('');
  const [openQuote, setOpenQuote] = useState('');
  const [debt, setDebt] = useState('');

  const cities = useMemo(() => Array.from(new Set(customers.map((c) => c.city))), [customers]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return customers.filter((c) => {
      const matchesQuery =
        !q ||
        c.name.toLowerCase().includes(q) ||
        c.contactPerson.toLowerCase().includes(q) ||
        c.phone.includes(q) ||
        c.email.toLowerCase().includes(q) ||
        c.city.toLowerCase().includes(q);
      const matchesSector = !sector || c.sector === sector;
      const matchesCity = !city || c.city === city;
      const matchesActiveProject = !activeProject || (activeProject === 'var' ? c.activeProjects > 0 : c.activeProjects === 0);
      const matchesOpenQuote = !openQuote || (openQuote === 'var' ? c.openQuotes > 0 : c.openQuotes === 0);
      const matchesDebt = !debt || (debt === 'var' ? c.receivable > 0 : c.receivable === 0);
      return matchesQuery && matchesSector && matchesCity && matchesActiveProject && matchesOpenQuote && matchesDebt;
    });
  }, [query, sector, city, activeProject, openQuote, debt, customers]);

  return (
    <div className="space-y-5 animate-fade-in-up">
      <PageHeader
        title="Müşteriler"
        description={`${filtered.length} müşteri listeleniyor`}
        actions={
          <button
            type="button"
            onClick={() => openCreate('customer')}
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            <i className="ri-add-line text-lg" /> Yeni Müşteri
          </button>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex min-w-[220px] flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3">
          <i className="ri-search-line text-base text-foreground-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Firma, yetkili, telefon ara..."
            className="h-9 w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
          />
        </div>
        <select value={sector} onChange={(e) => setSector(e.target.value)} className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-700">
          <option value="">Tüm Sektörler</option>
          {sectors.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={city} onChange={(e) => setCity(e.target.value)} className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-700">
          <option value="">Tüm Şehirler</option>
          {cities.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={activeProject} onChange={(e) => setActiveProject(e.target.value)} className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-700">
          <option value="">Aktif Proje: Tümü</option>
          <option value="var">Aktif Projesi Var</option>
          <option value="yok">Aktif Projesi Yok</option>
        </select>
        <select value={openQuote} onChange={(e) => setOpenQuote(e.target.value)} className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-700">
          <option value="">Açık Teklif: Tümü</option>
          <option value="var">Açık Teklifi Var</option>
          <option value="yok">Açık Teklifi Yok</option>
        </select>
        <select value={debt} onChange={(e) => setDebt(e.target.value)} className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-700">
          <option value="">Alacak: Tümü</option>
          <option value="var">Alacağı Var</option>
          <option value="yok">Alacağı Yok</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="Müşteri bulunamadı" description="Filtreleri değiştirerek tekrar deneyin." />
      ) : (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/50 text-left text-xs font-semibold uppercase tracking-wide text-foreground-500">
                  <th className="px-5 py-3">Firma</th>
                  <th className="px-4 py-3">Yetkili</th>
                  <th className="px-4 py-3">İletişim</th>
                  <th className="px-4 py-3">Şehir</th>
                  <th className="px-4 py-3">Sektör</th>
                  <th className="px-4 py-3 text-center">Aktif Proje</th>
                  <th className="px-4 py-3 text-center">Açık Teklif</th>
                  <th className="px-4 py-3 text-right">Toplam Satış</th>
                  <th className="px-4 py-3 text-right">Alacak</th>
                  <th className="px-4 py-3">Son Görüşme</th>
                  <th className="px-4 py-3 text-right">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/customers/${c.id}`)}
                    className="cursor-pointer border-b border-background-100 transition-colors last:border-0 hover:bg-background-100/60"
                  >
                    <td className="px-5 py-3.5">
                      <p className="font-semibold text-foreground-900">{c.name}</p>
                      <p className="text-xs text-foreground-500">{c.id}</p>
                    </td>
                    <td className="px-4 py-3.5 text-foreground-700">{c.contactPerson}</td>
                    <td className="px-4 py-3.5">
                      <p className="text-foreground-700">{c.phone}</p>
                      <p className="text-xs text-foreground-500">{c.email}</p>
                    </td>
                    <td className="px-4 py-3.5 text-foreground-700">{c.city}</td>
                    <td className="px-4 py-3.5">
                      <Badge>{c.sector}</Badge>
                    </td>
                    <td className="px-4 py-3.5 text-center text-foreground-700">{c.activeProjects}</td>
                    <td className="px-4 py-3.5 text-center text-foreground-700">{c.openQuotes}</td>
                    <td className="px-4 py-3.5 text-right font-semibold text-foreground-900">{formatTL(c.totalSales)}</td>
                    <td className="px-4 py-3.5 text-right font-semibold text-accent-700">{c.receivable > 0 ? formatTL(c.receivable) : '-'}</td>
                    <td className="px-4 py-3.5 text-foreground-600">{formatDate(c.lastContact)}</td>
                    <td className="px-4 py-3.5"><RowActions onEdit={() => openEdit('customer', c)} onDelete={() => openDelete('customer', c.id, c.name)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}